import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ClientApiService } from '@aiwyn/api';
import { AddOn, AddOnInputType, AddOnOption } from '../../../../model';
import { Client } from '@aiwyn/shared';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { maxBy } from 'lodash';
import { PaymentsState } from '../../state';

@Component({
  selector: 'app-additional-service',
  templateUrl: './additional-service.component.html',
  styleUrls: ['./additional-service.component.scss'],
  standalone: false,
})
export class AdditionalServiceComponent {
  // get accounts (clients) and set default value if only one exists
  accounts$: Observable<Client[]> = this.clientApiService.getClientsFromClientIdList().pipe(
    tap(accounts => {
      if (accounts.length === 1) {
        this.accountControl.setValue(accounts[0]); // idk
      }
    }),
  );

  // initialize form controls
  accountControl = this.initializeFormControl(null);
  typeControl = this.initializeFormControl(null);
  notesControl = this.initializeFormControl('', [Validators.maxLength(140)]);
  paymentAmountControl = this.initializeFormControl(0, [Validators.min(0.01)]);

  // initialize form group
  form = this.fb.group({
    account: this.accountControl,
    type: this.typeControl,
    notes: this.notesControl,
    paymentAmount: this.paymentAmountControl,
  });

  AddOnInputType = AddOnInputType;

  constructor(
    private fb: UntypedFormBuilder,
    private readonly store: Store,
    private dialogRef: MatDialogRef<AdditionalServiceComponent>,
    private clientApiService: ClientApiService,
    @Inject(MAT_DIALOG_DATA) public data: { addOnOptions: AddOnOption[] },
  ) {
    this.typeControl.setValue(
      data.addOnOptions.find(option => option.type.toUpperCase() === 'MISCELLANEOUS') || data.addOnOptions[0],
    );
  }

  /**
   * Create form control based on data provided. All are required by default and can't be overridden.
   * @param initialValue initial value to which form control is set
   * @param optionalValidators optional, additional validators for form control
   * @returns FormControl
   */
  initializeFormControl(
    initialValue: string | number | AddOnOption = '',
    optionalValidators: ValidatorFn[] = [],
  ): UntypedFormControl {
    return this.fb.control(initialValue, [Validators.required, ...optionalValidators]);
  }

  /**
   * Close dialog without submitting.
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Submit additional service (add-on). Close dialog and pass along add-on.
   */
  submit(): void {
    // ensure validation shows in UI if form is pristine
    this.form.markAllAsTouched();
    this.form.markAsDirty();

    // do nothing if form is invalid
    if (this.form.invalid) {
      return;
    }

    // create add-on object
    const addOn: AddOn = {
      id: maxBy(this.store.selectSnapshot(PaymentsState.getAddOns), 'id')?.id ?? 0,
      account: this.accountControl.value,
      type: this.typeControl.value.type,
      name: this.typeControl.value.input.label,
      paymentAmount: this.paymentAmountControl.value,
      notes: this.notesControl.value,
    };

    // close dialog and pass along add-on
    this.dialogRef.close(addOn);
  }
}
